/* eslint-disable no-console */

import {
  createSelector,
  createSelectorFactory,
  defaultMemoize,
  MemoizedSelector,
} from '@ngrx/store';
import { AnyFn, ComparatorFn, MemoizedProjection } from '@ngrx/store/src/selector';
import deepEqual from 'fast-deep-equal';

interface MemoizeOptions {
  projectorFn: Function;
}

export function isEqualCheck(a: any, b: any): boolean {
  return a === b;
}

function isArgumentsChanged(currentArgs: any[], previousArgs: any[], comparator: ComparatorFn) {
  if (!previousArgs) return true;
  if (currentArgs.length !== previousArgs.length) return true;

  for (let i = 0; i < currentArgs.length; i++) {
    if (!comparator(currentArgs[i], previousArgs[i])) {
      return true;
    }
  }
  return false;
}

enum DebugMode {
  Disabled,
  Info,
  Verbose,
}

const debugConfig = {
  mode: DebugMode.Disabled,
};

const debugStateChanged = (options: MemoizeOptions, stateChunks: any[], lastStateChunks: any[]) => {
  const name = options.projectorFn.name || (<any>options.projectorFn).debugName;
  name
    ? console.log(
        `%c"${name}" projector called state changed`,
        'background: red; padding: 2px 4px;',
      )
    : console.log('%cProjector called state changed', 'background: red; padding: 2px 4px;');
  if (DebugMode.Verbose <= debugConfig.mode) {
    console.log({ projector: options.projectorFn });
    console.log({ lastStateChunks, stateChunks });
  }
};

// eslint-disable-next-line max-lines-per-function
export const memoize = (options: MemoizeOptions) =>
  // eslint-disable-next-line max-lines-per-function
  function (projectionFn: AnyFn): MemoizedProjection {
    let lastResult: any = null;
    let overrideResult: any;
    let lastStateChunks: any[];

    function reset() {
      lastStateChunks = [];
      lastResult = null;
    }

    function setResult(result: any = undefined) {
      overrideResult = { result };
    }

    function clearResult() {
      overrideResult = undefined;
    }

    function memoized(...args: any[]): any {
      if (overrideResult !== undefined) {
        return overrideResult.result;
      }

      const stateChunks = args;
      const stateChanged = isArgumentsChanged(stateChunks, lastStateChunks, isEqualCheck);
      if (!stateChanged) {
        return lastResult;
      } else {
        if (DebugMode.Disabled !== debugConfig.mode && lastStateChunks) {
          debugStateChanged(options, stateChunks, lastStateChunks);
        }
        lastResult = null;
        lastStateChunks = stateChunks;
      }

      // eslint-disable-next-line prefer-spread
      const newResult = projectionFn.apply(null, arguments);
      lastResult = newResult;
      return newResult;
    }

    if (DebugMode.Disabled !== debugConfig.mode) {
      memoized.__options__ = options;
    }

    return { memoized, reset, setResult, clearResult };
  };

export const memoizeSelector = <Factory extends (props: any) => MemoizedSelector<any, any>>(
  selectorFactory: Factory,
): Factory => {
  const primitiveCache = new Map();
  const objectCache = new WeakMap();

  return <Factory>(<unknown>function () {
    const props = arguments[0];
    const cache = typeof props === 'object' && props !== null ? objectCache : primitiveCache;

    if (cache.has(props)) {
      return cache.get(props);
    }

    const result = selectorFactory(props);
    cache.set(props, result);
    return result;
  });
};

export const createFeatureSelector = <T>(path: string) =>
  createSelector<any, any, T>(
    (state: any) => state[path],
    (f: any) => f,
  );

export const namedProjector = <T extends (...args: any[]) => any>(
  name: string,
  projector: T,
): T => {
  (<any>projector).debugName = name;
  return projector;
};

export const createSelectorDeepEqual: typeof createSelector = function () {
  // eslint-disable-next-line prefer-spread
  return createSelectorFactory((projector: any) =>
    defaultMemoize(projector, deepEqual, deepEqual),
  ).apply(null, arguments);
};

export { DebugMode as RtNgrxDebugMode, debugConfig as rtNgrxDebugConfig };
